import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import AdminCarForm from './AdminCarForm';
import AdminAds from './AdminAds';
import AdminSiteSettings from './AdminSiteSettings';
import AdminBulkPriceUpdate from './AdminBulkPriceUpdate';
import AdminLeads from './AdminLeads';
import AdminAffiliates from './AdminAffiliates';
import AdminReviews from './AdminReviews';
import AdminSocialLinks from './AdminSocialLinks';
import AdminGuaziImport from './AdminGuaziImport';
import * as LuIcons from 'react-icons/lu';
import SafeIcon from '../../common/SafeIcon';

const { LuPlus, LuEdit, LuTrash2, LuCar, LuLogOut, LuMegaphone, LuSettings, LuEye, LuCopy, LuCheckCircle, LuClock, LuSearch, LuX, LuCheck, LuSlidersHorizontal, LuAlertTriangle, LuZap, LuPhoneCall, LuUsers, LuVideo, LuShare2 } = LuIcons;

const TABS = [
  { id: 'cars', label: 'السيارات', icon: LuCar },
  { id: 'guazi', label: 'استيراد Guazi', icon: LuZap },
  { id: 'leads', label: 'الطلبات', icon: LuPhoneCall },
  { id: 'affiliates', label: 'المسوقين', icon: LuUsers },
  { id: 'bulk', label: 'تحديث الأسعار', icon: LuZap },
  { id: 'ads', label: 'الإعلانات', icon: LuMegaphone },
  { id: 'reviews', label: 'المراجعات', icon: LuVideo },
  { id: 'social', label: 'التواصل', icon: LuShare2 },
  { id: 'settings', label: 'إعدادات الموقع', icon: LuSettings },
];

const STATUS_OPTIONS = [
  { val: 'all', label: 'الكل' },
  { val: 'available', label: 'متوفرة' },
  { val: 'sold', label: 'مبيعة' },
];

const CONDITION_OPTIONS = [
  { val: 'all', label: 'الكل' },
  { val: 'new', label: 'جديدة' },
  { val: 'used', label: 'مستعملة' },
];

const ConfirmDialog = ({ open, title, message, onConfirm, onCancel, danger }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4 ${danger ? 'bg-red-100' : 'bg-amber-100'}`}>
          <SafeIcon icon={LuAlertTriangle} className={`w-6 h-6 ${danger ? 'text-red-600' : 'text-amber-600'}`} />
        </div>
        <h3 className="text-lg font-black text-slate-900 text-center mb-2">{title}</h3>
        <p className="text-sm text-slate-500 text-center mb-6">{message}</p>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 py-2.5 rounded-xl border-2 border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50">إلغاء</button>
          <button onClick={onConfirm} className={`flex-1 py-2.5 rounded-xl font-bold text-sm text-white ${danger ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-500 hover:bg-amber-600'}`}>تأكيد</button>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, color, icon }) => (
  <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${color}`}>
    <SafeIcon icon={icon} className="w-5 h-5 flex-shrink-0" />
    <div>
      <p className="text-xs font-medium opacity-70">{label}</p>
      <p className="text-xl font-black">{value}</p>
    </div>
  </div>
);

// التبويب من الرابط: /dash-dz-2026/<tab> — حتى يمكن نسخ رابط أي قسم والعودة إليه
const ADMIN_BASE = '/dash-dz-2026';
const tabFromUrl = () => {
  if (window.location.search.includes('guazi=')) return 'guazi';
  const seg = window.location.pathname.slice(ADMIN_BASE.length).split('/').filter(Boolean)[0] || '';
  return TABS.some((t) => t.id === seg) ? seg : 'cars';
};

const AdminDashboard = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState(tabFromUrl);

  // تغيير التبويب يكتب الرابط، وأزرار رجوع/تقدم المتصفح تعيد التبويب
  const goTab = (id) => {
    setActiveTab(id);
    const url = id === 'cars' ? ADMIN_BASE : `${ADMIN_BASE}/${id}`;
    if (window.location.pathname !== url) window.history.pushState({}, '', url);
  };
  useEffect(() => {
    const onPop = () => setActiveTab(tabFromUrl());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [carToEdit, setCarToEdit] = useState(null);
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCondition, setFilterCondition] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirm, setConfirm] = useState({ open: false, title: '', message: '', onConfirm: null, danger: false });

  const fetchCars = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('cars_for_sale').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      setCars(data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'cars') fetchCars();
  }, [activeTab]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [searchTerm, filterStatus, filterCondition]);

  const filteredCars = useMemo(() => cars.filter(car => {
    const matchSearch = !searchTerm || car.brand.toLowerCase().includes(searchTerm.toLowerCase()) || car.model.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = filterStatus === 'all' || car.status === filterStatus || (!car.status && filterStatus === 'available');
    const matchCondition = filterCondition === 'all' || car.condition === filterCondition;
    return matchSearch && matchStatus && matchCondition;
  }), [cars, searchTerm, filterStatus, filterCondition]);

  const stats = useMemo(() => ({
    total: cars.length,
    available: cars.filter(c => c.status !== 'sold').length,
    sold: cars.filter(c => c.status === 'sold').length,
    views: cars.reduce((s, c) => s + (c.views_count || 0), 0),
  }), [cars]);

  const allFilteredSelected = filteredCars.length > 0 && filteredCars.every(c => selectedIds.has(c.id));
  const someSelected = selectedIds.size > 0;

  const toggleSelectAll = () => {
    if (allFilteredSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredCars.map(c => c.id)));
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDelete = (id) => {
    setConfirm({
      open: true,
      danger: true,
      title: 'حذف السيارة',
      message: 'هل أنت متأكد من حذف هذه السيارة؟ لا يمكن التراجع.',
      onConfirm: async () => {
        await supabase.from('cars_for_sale').delete().eq('id', id);
        setConfirm(c => ({ ...c, open: false }));
        fetchCars();
      }
    });
  };

  const handleBulkDelete = () => {
    const ids = [...selectedIds];
    setConfirm({
      open: true,
      danger: true,
      title: `حذف ${ids.length} سيارة`,
      message: `هل أنت متأكد من حذف ${ids.length} سيارة؟`,
      onConfirm: async () => {
        await supabase.from('cars_for_sale').delete().in('id', ids);
        setSelectedIds(new Set());
        setConfirm(c => ({ ...c, open: false }));
        fetchCars();
      }
    });
  };

  const handleBulkStatus = (status) => {
    const ids = [...selectedIds];
    const label = status === 'sold' ? 'تم البيع' : 'متوفرة';
    setConfirm({
      open: true,
      danger: false,
      title: `تغيير حالة ${ids.length} سيارة`,
      message: `هل تريد تغيير حالة ${ids.length} سيارة إلى "${label}"؟`,
      onConfirm: async () => {
        await supabase.from('cars_for_sale').update({ status }).in('id', ids);
        setSelectedIds(new Set());
        setConfirm(c => ({ ...c, open: false }));
        fetchCars();
      }
    });
  };

  const handleToggleStatus = async (car) => {
    const newStatus = car.status === 'sold' ? 'available' : 'sold';
    await supabase.from('cars_for_sale').update({ status: newStatus }).eq('id', car.id);
    fetchCars();
  };

  const handleDuplicate = async (car) => {
    setDuplicatingId(car.id);
    try {
      const { id, created_at, views_count, ...rest } = car;
      const { error } = await supabase.from('cars_for_sale').insert([{ ...rest, model: rest.model + ' (نسخة)', views_count: 0, status: 'available' }]);
      if (error) throw error;
      fetchCars();
    } catch (err) {
      alert(`خطأ في النسخ: ${err.message}`);
    } finally {
      setDuplicatingId(null);
    }
  };

  const hasActiveFilters = filterStatus !== 'all' || filterCondition !== 'all' || !!searchTerm;

  return (
    <div className="flex-1 max-w-7xl w-full mx-auto px-3 sm:px-6 lg:px-8 py-6">
      <div className="flex justify-between items-center mb-6 bg-white p-3 sm:p-4 rounded-2xl shadow-sm border border-slate-200 flex-wrap gap-2">
        <div className="flex gap-1 flex-wrap">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => goTab(tab.id)} className={`px-3 py-2 rounded-xl font-bold flex items-center transition-colors text-xs sm:text-sm ${activeTab === tab.id ? 'bg-emerald-100 text-emerald-800' : 'text-slate-600 hover:bg-slate-50'}`}>
              <SafeIcon icon={tab.icon} className="w-4 h-4 me-1.5" />
              <span className="hidden sm:inline">{tab.label}</span>
              <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
            </button>
          ))}
        </div>
        <button onClick={onLogout} className="flex items-center bg-red-50 hover:bg-red-100 text-red-600 px-3 py-2 rounded-xl font-medium transition-colors text-sm">
          <SafeIcon icon={LuLogOut} className="w-4 h-4 me-1.5" /> <span className="hidden sm:inline">خروج</span>
        </button>
      </div>

      {activeTab === 'guazi' && <AdminGuaziImport />}
      {activeTab === 'bulk' && <AdminBulkPriceUpdate />}
      {activeTab === 'ads' && <AdminAds />}
      {activeTab === 'settings' && <AdminSiteSettings />}
      {activeTab === 'leads' && <AdminLeads />}
      {activeTab === 'affiliates' && <AdminAffiliates />}
      {activeTab === 'reviews' && <AdminReviews />}
      {activeTab === 'social' && <AdminSocialLinks />}

      {activeTab === 'cars' && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <StatCard label="إجمالي" value={stats.total} icon={LuCar} color="border-slate-200 text-slate-700 bg-white" />
            <StatCard label="متوفرة" value={stats.available} icon={LuClock} color="border-emerald-200 text-emerald-700 bg-emerald-50" />
            <StatCard label="مبيعة" value={stats.sold} icon={LuCheckCircle} color="border-red-200 text-red-700 bg-red-50" />
            <StatCard label="مشاهدات" value={stats.views.toLocaleString()} icon={LuEye} color="border-violet-200 text-violet-700 bg-violet-50" />
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-4">
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between mb-3">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-black text-slate-900">إدارة السيارات</h1>
                {someSelected && (
                  <span className="bg-emerald-100 text-emerald-700 text-xs font-black px-2.5 py-1 rounded-full">
                    {selectedIds.size} محدد
                  </span>
                )}
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => setShowFilters(o => !o)} className={`flex items-center gap-2 px-3 py-2 rounded-xl border-2 font-bold text-sm transition-all ${hasActiveFilters ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                  <SafeIcon icon={LuSlidersHorizontal} className="w-4 h-4" /> فلترة
                </button>
                <button onClick={() => { setCarToEdit(null); setIsFormOpen(true); }} className="flex items-center bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-xl font-bold text-sm shadow-sm">
                  <SafeIcon icon={LuPlus} className="w-4 h-4 me-1" /> إضافة سيارة
                </button>
              </div>
            </div>

            <div className="relative">
              <SafeIcon icon={LuSearch} className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="البحث بالماركة أو الموديل..." className="w-full pe-10 ps-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none text-sm" />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <SafeIcon icon={LuX} className="w-4 h-4" />
                </button>
              )}
            </div>

            {showFilters && (
              <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-2">حالة الإعلان</label>
                  <div className="flex gap-1.5">
                    {STATUS_OPTIONS.map(opt => (
                      <button key={opt.val} onClick={() => setFilterStatus(opt.val)} className={`flex-1 py-2 rounded-xl text-xs font-bold border-2 transition-all ${filterStatus === opt.val ? 'border-slate-400 bg-slate-900 text-white' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-2">حالة السيارة</label>
                  <div className="flex gap-1.5">
                    {CONDITION_OPTIONS.map(opt => (
                      <button key={opt.val} onClick={() => setFilterCondition(opt.val)} className={`flex-1 py-2 rounded-xl text-xs font-bold border-2 transition-all ${filterCondition === opt.val ? 'border-slate-400 bg-slate-900 text-white' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                {hasActiveFilters && (
                  <button onClick={() => { setFilterStatus('all'); setFilterCondition('all'); setSearchTerm(''); }} className="sm:col-span-2 text-xs text-red-500 hover:text-red-700 font-bold flex items-center gap-1">
                    <SafeIcon icon={LuX} className="w-3 h-3" /> مسح جميع الفلاتر
                  </button>
                )}
              </div>
            )}

            <div className="mt-2 flex items-center justify-between">
              <p className="text-xs text-slate-400 font-medium">
                عرض <span className="text-slate-700 font-black">{filteredCars.length}</span> من {cars.length} سيارة
              </p>
              {someSelected && (
                <div className="flex items-center gap-2">
                  <button onClick={() => handleBulkStatus('available')} className="text-xs bg-emerald-100 text-emerald-700 hover:bg-emerald-200 px-2.5 py-1 rounded-lg font-bold flex items-center gap-1">
                    <SafeIcon icon={LuClock} className="w-3 h-3" /> متوفرة
                  </button>
                  <button onClick={() => handleBulkStatus('sold')} className="text-xs bg-orange-100 text-orange-700 hover:bg-orange-200 px-2.5 py-1 rounded-lg font-bold flex items-center gap-1">
                    <SafeIcon icon={LuCheckCircle} className="w-3 h-3" /> مباعة
                  </button>
                  <button onClick={handleBulkDelete} className="text-xs bg-red-100 text-red-700 hover:bg-red-200 px-2.5 py-1 rounded-lg font-bold flex items-center gap-1">
                    <SafeIcon icon={LuTrash2} className="w-3 h-3" /> حذف
                  </button>
                  <button onClick={() => setSelectedIds(new Set())} className="text-slate-400 hover:text-slate-600">
                    <SafeIcon icon={LuX} className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            {loading ? (
              <div className="p-10 text-center">
                <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-slate-500 text-sm">جاري التحميل...</p>
              </div>
            ) : filteredCars.length === 0 ? (
              <div className="p-16 text-center">
                <SafeIcon icon={LuCar} className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                <p className="text-slate-400 font-medium">لا توجد سيارات مطابقة</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-100">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3.5 w-10">
                        <button onClick={toggleSelectAll} className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${allFilteredSelected ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300 hover:border-emerald-400'}`}>
                          {allFilteredSelected && <SafeIcon icon={LuCheck} className="w-3 h-3 text-white" />}
                        </button>
                      </th>
                      <th className="px-4 py-3.5 text-start text-xs font-black text-slate-500 uppercase">السيارة</th>
                      <th className="px-4 py-3.5 text-start text-xs font-black text-slate-500 uppercase">الحالة</th>
                      <th className="px-4 py-3.5 text-start text-xs font-black text-slate-500 uppercase">النوع</th>
                      <th className="px-4 py-3.5 text-start text-xs font-black text-slate-500 uppercase">السعر</th>
                      <th className="px-4 py-3.5 text-start text-xs font-black text-slate-500 uppercase">السنة / الممشى</th>
                      <th className="px-4 py-3.5 text-start text-xs font-black text-slate-500 uppercase">المشاهدات</th>
                      <th className="px-4 py-3.5 text-end text-xs font-black text-slate-500 uppercase">إجراءات</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {filteredCars.map(car => {
                      const isSelected = selectedIds.has(car.id);
                      const isSold = car.status === 'sold';
                      return (
                        <tr key={car.id} className={`transition-colors ${isSelected ? 'bg-emerald-50/60' : isSold ? 'bg-slate-50/50 opacity-80' : 'hover:bg-slate-50/50'}`}>
                          <td className="px-4 py-3.5">
                            <button onClick={() => toggleSelect(car.id)} className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${isSelected ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300 hover:border-emerald-400'}`}>
                              {isSelected && <SafeIcon icon={LuCheck} className="w-3 h-3 text-white" />}
                            </button>
                          </td>
                          <td className="px-4 py-3.5">
                            <div className="flex items-center gap-3">
                              <div className="h-11 w-11 flex-shrink-0 bg-slate-100 rounded-xl overflow-hidden flex items-center justify-center">
                                {car.image_url ? <img src={car.image_url} alt="" className="h-full w-full object-cover" /> : <SafeIcon icon={LuCar} className="h-5 w-5 text-slate-400" />}
                              </div>
                              <div>
                                <div className="font-black text-slate-900 text-sm">{car.brand} {car.model}</div>
                                <div className="text-xs text-slate-400 mt-0.5">
                                  {(Array.isArray(car.gallery) ? car.gallery.length : 0) + (car.image_url ? 1 : 0)} صورة
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3.5">
                            <button onClick={() => handleToggleStatus(car)} className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs font-bold transition-all border-2 ${isSold ? 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100' : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${isSold ? 'bg-red-500' : 'bg-emerald-500'}`} />
                              {isSold ? 'مباع' : 'متوفر'}
                            </button>
                          </td>
                          <td className="px-4 py-3.5">
                            <span className={`text-xs font-bold px-2 py-1 rounded-lg ${car.condition === 'new' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                              {car.condition === 'new' ? 'جديدة' : 'مستعملة'}
                            </span>
                          </td>
                          <td className="px-4 py-3.5 whitespace-nowrap text-emerald-600 font-black text-sm">
                            {Number(car.price).toLocaleString()} <span className="text-xs font-normal text-slate-400">دج</span>
                          </td>
                          <td className="px-4 py-3.5 whitespace-nowrap text-xs text-slate-500">
                            {car.year} • {Number(car.mileage).toLocaleString()} كم
                          </td>
                          <td className="px-4 py-3.5 whitespace-nowrap">
                            <div className="flex items-center gap-1 text-slate-500">
                              <SafeIcon icon={LuEye} className="w-3.5 h-3.5" />
                              <span className="text-sm font-bold">{(car.views_count || 0).toLocaleString()}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3.5 whitespace-nowrap text-end">
                            <div className="flex items-center justify-end gap-1">
                              <button onClick={() => handleDuplicate(car)} disabled={duplicatingId === car.id} className="text-amber-600 hover:text-amber-800 bg-amber-50 hover:bg-amber-100 p-2 rounded-lg transition-colors disabled:opacity-50">
                                <SafeIcon icon={LuCopy} className="w-4 h-4" />
                              </button>
                              <button onClick={() => { setCarToEdit(car); setIsFormOpen(true); }} className="text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 p-2 rounded-lg transition-colors">
                                <SafeIcon icon={LuEdit} className="w-4 h-4" />
                              </button>
                              <button onClick={() => handleDelete(car.id)} className="text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 p-2 rounded-lg transition-colors">
                                <SafeIcon icon={LuTrash2} className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {isFormOpen && (
            <AdminCarForm carToEdit={carToEdit} onClose={() => setIsFormOpen(false)} onSave={() => { setIsFormOpen(false); fetchCars(); }} />
          )}
        </>
      )}

      <ConfirmDialog open={confirm.open} title={confirm.title} message={confirm.message} danger={confirm.danger} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(c => ({ ...c, open: false }))} />
    </div>
  );
};

export default AdminDashboard;